import type { FieldErrors, FieldValues } from "react-hook-form";
import type { BaseSyntheticEvent } from "react";
import { toast } from "@/hooks/use-toast";

type FormError = { message?: unknown };

function firstError(errors: Record<string, unknown>, prefix = ""): { field: string; message: string } | null {
  for (const [key, value] of Object.entries(errors)) {
    if (key === "ref" || key === "types") continue;
    const field = prefix ? `${prefix}.${key}` : key;
    if (!value || typeof value !== "object") continue;
    const error = value as FormError;
    if (typeof error.message === "string" && error.message.trim()) {
      return { field, message: error.message };
    }
    if ("type" in value) return { field, message: "Controlla questo campo." };
    const nested = firstError(value as Record<string, unknown>, field);
    if (nested) return nested;
  }
  return null;
}

/** Keep validation feedback visible even when the invalid control is above a fixed submit footer. */
export function showInvalidFormFeedback<T extends FieldValues>(
  errors: FieldErrors<T>,
  event?: BaseSyntheticEvent,
): void {
  const error = firstError(errors as Record<string, unknown>);
  const message = error?.message || "Controlla i campi obbligatori.";
  const form = event?.target instanceof HTMLFormElement ? event.target : null;
  if (!form || !error) {
    toast({ title: "Controlla il modulo", description: message, variant: "destructive" });
    return;
  }
  requestAnimationFrame(() => {
    const escaped = CSS.escape(error.field);
    const field = form.querySelector<HTMLElement>(
      `[data-form-field="${escaped}"], [name="${escaped}"], [id="${escaped}"]`,
    );
    const inlineMessage = field?.querySelector<HTMLElement>("[id$='-form-item-message'], .text-destructive")
      ?.textContent?.trim();
    toast({ title: "Controlla il modulo", description: inlineMessage || message, variant: "destructive" });
    if (!field) return;
    field.scrollIntoView({ behavior: "smooth", block: "center" });
    const control = field.matches("input, textarea, select, button, [role='combobox']")
      ? field
      : field.querySelector<HTMLElement>("input, textarea, select, button, [role='combobox']");
    control?.focus({ preventScroll: true });
  });
}
